<!-- src/components/InstallPWA.vue -->
<template>
  <div class="install-pwa-screen">
    <div class="content">
      <div class="logo-container">
        <img src="@assets/logo_white.png" alt="노루스마트컬러 로고" class="logo" />
      </div>
      
      <div class="info-container">
        <h1 class="title">노루스마트컬러</h1>
        <p class="description">
          더 나은 경험을 위해<br />
          앱을 설치해주세요
        </p>
      </div>

      <!-- iOS용 설치 안내 -->
      <div v-if="isIOS" class="ios-install-guide">
        <div class="guide-steps">
          <p class="guide-text">Safari에서 아래 버튼을 클릭하세요:</p>
          
          <div class="share-icon-container">
            <svg class="share-icon" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
              <rect x="15" y="28" width="20" height="18" rx="2" fill="none" stroke="white" stroke-width="2"/>
              <path d="M25 28 L25 8 M18 15 L25 8 L32 15" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="icon-label">공유 버튼</span>
          </div>

          <div class="step-arrow">↓</div>

          <div class="step-box">
            <p class="step-text">
              <strong>"홈 화면에 추가"</strong>를<br />
              선택해주세요
            </p>
          </div>
        </div>
      </div>

      <!-- 삼성 브라우저 수동 설치 안내 -->
      <div v-else-if="showManualInstallGuide" class="ios-install-guide">
        <div class="guide-steps">
          <p class="guide-text">아래 방법으로 설치해주세요:</p>
          
          <div class="share-icon-container">
            <svg class="share-icon" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
              <line x1="25" y1="8" x2="25" y2="38" stroke="white" stroke-width="3" stroke-linecap="round"/>
              <line x1="10" y1="23" x2="40" y2="23" stroke="white" stroke-width="3" stroke-linecap="round"/>
            </svg>
            <span class="icon-label">메뉴(≡) → 페이지 추가</span>
          </div>

          <div class="step-arrow">↓</div>

          <div class="step-box">
            <p class="step-text">
              <strong>"홈 화면"</strong>을<br />
              선택해주세요
            </p>
          </div>
        </div>
      </div>

      <!-- Android/Desktop용 설치 버튼 -->
      <button 
        v-else
        @click="installPWA" 
        class="install-button"
        :disabled="!deferredPrompt"
      >
        {{ buttonText }}
      </button>
      
    </div>

    <div class="logo-sub-container">
      <img src="@assets/logo_sub_white.png" alt="노루페인트" class="logo-sub" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'InstallPWA',
  data() {
    return {
      deferredPrompt: null,
      isIOS: false,
      isAlreadyInstalled: false,
      checkTimeout: null
    }
  },
  computed: {
    buttonText() {
      if (this.deferredPrompt) {
        return '앱 설치하기'
      } else if (this.isAlreadyInstalled) {
        return '이미 설치된 앱입니다'
      } else {
        return '설치 준비 중...'
      }
    }
  },
  mounted() {
    // iOS 감지 (Safari 전용)
    const userAgent = navigator.userAgent
    this.isIOS = (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) ||
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

    console.log('iOS 기기:', this.isIOS)

    // 이미 설치된 앱인지 확인 (지원되는 브라우저에서만)
    this.checkIfAlreadyInstalled()

    // beforeinstallprompt 이벤트 리스너 (Android/Desktop)
    window.addEventListener('beforeinstallprompt', (e) => {
      // 기본 설치 프롬프트 방지
      e.preventDefault()
      // 나중에 사용하기 위해 이벤트 저장
      this.deferredPrompt = e
      this.isAlreadyInstalled = false
      console.log('설치 프롬프트 준비됨')
      
      // 타임아웃 취소
      if (this.checkTimeout) {
        clearTimeout(this.checkTimeout)
        this.checkTimeout = null
      }
    })

    // 앱이 설치되었을 때
    window.addEventListener('appinstalled', () => {
      console.log('PWA가 설치되었습니다')
      this.isAlreadyInstalled = true
      this.deferredPrompt = null
    })

    // 타임아웃: 3초 후에도 프롬프트가 없으면 이미 설치된 것으로 간주
    this.checkTimeout = setTimeout(() => {
      if (!this.deferredPrompt && !this.isIOS) {
        console.log('설치 프롬프트가 없음 - 앱이 이미 설치되었거나 지원되지 않는 브라우저')
        this.isAlreadyInstalled = true
      }
    }, 3000)
  },
  beforeUnmount() {
    if (this.checkTimeout) {
      clearTimeout(this.checkTimeout)
      this.checkTimeout = null
    }
  },
  methods: {
    async checkIfAlreadyInstalled() {
      // getInstalledRelatedApps API 지원 여부 확인
      if ('getInstalledRelatedApps' in navigator) {
        try {
          const relatedApps = await navigator.getInstalledRelatedApps()
          console.log('관련 설치된 앱:', relatedApps)
          if (relatedApps.length > 0) {
            this.isAlreadyInstalled = true
            console.log('앱이 이미 설치되어 있습니다')
          }
        } catch (error) {
          console.log('getInstalledRelatedApps 확인 실패:', error)
        }
      }
    },
    async installPWA() {
      if (!this.deferredPrompt) {
        console.log('설치 프롬프트를 사용할 수 없습니다')
        return
      }

      // 설치 프롬프트 표시
      this.deferredPrompt.prompt()

      // 사용자 선택 대기
      const { outcome } = await this.deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        console.log('사용자가 PWA 설치를 수락했습니다')
      } else {
        console.log('사용자가 PWA 설치를 거부했습니다')
      }

      // 프롬프트는 한 번만 사용 가능
      this.deferredPrompt = null
    }
  }
}
</script>

<style scoped>
.install-pwa-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  background: linear-gradient(135deg, #007680 0%, #005a63 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  z-index: 9999;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 40px 0 30px 0;
  box-sizing: border-box;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 30px;
  max-width: 500px;
  width: 100%;
  flex: 1;
}

.logo-container {
  margin-bottom: 40px;
  animation: fadeInDown 0.8s ease-out;
}

.logo {
  width: 200px;
  height: auto;
  object-fit: contain;
}

.info-container {
  text-align: center;
  margin-bottom: 50px;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
}

.title {
  color: white;
  font-size: 28px;
  font-weight: bold;
  margin: 0 0 20px 0;
}

.description {
  color: rgba(255, 255, 255, 0.9);
  font-size: 18px;
  line-height: 1.6;
  margin: 0;
}

/* iOS 설치 안내 */
.ios-install-guide {
  width: 100%;
  max-width: 350px;
  animation: fadeInUp 0.8s ease-out 0.4s backwards;
}

.guide-steps {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.guide-text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 16px;
  text-align: center;
  margin: 0 0 10px 0;
  font-weight: 500;
}

.share-icon-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  padding: 30px;
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  animation: pulse 2s ease-in-out infinite;
}

.share-icon {
  width: 60px;
  height: 60px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.icon-label {
  color: white;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
}

.step-arrow {
  color: white;
  font-size: 36px;
  font-weight: bold;
  margin: 5px 0;
  animation: bounce 1.5s ease-in-out infinite;
}

.step-box {
  background-color: rgba(255, 255, 255, 0.15);
  padding: 25px 30px;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  width: 100%;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.step-text {
  color: white;
  font-size: 17px;
  text-align: center;
  margin: 0;
  line-height: 1.6;
}

.step-text strong {
  color: #FFD700;
  font-size: 18px;
}

/* Android/Desktop 설치 버튼 */
.install-button {
  width: 100%;
  max-width: 300px;
  padding: 16px 32px;
  background-color: white;
  color: #007680;
  border: none;
  border-radius: 50px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  animation: fadeInUp 0.8s ease-out 0.6s backwards;
}

.install-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.install-button:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.install-button:not(:disabled):active {
  transform: translateY(0);
}

.logo-sub-container {
  flex-shrink: 0;
  padding: 20px 0;
  animation: fadeIn 1s ease-out 0.8s backwards;
}

.logo-sub {
  width: 150px;
  height: auto;
  object-fit: contain;
  opacity: 0.9;
}

/* 애니메이션 */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(255, 255, 255, 0.3);
  }
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(10px);
  }
}

/* 모바일 반응형 */
@media (max-width: 480px) {
  .content {
    padding: 0 20px;
  }

  .logo {
    width: 160px;
  }

  .title {
    font-size: 24px;
  }

  .description {
    font-size: 16px;
  }

  .ios-install-guide {
    max-width: 100%;
  }

  .guide-text {
    font-size: 15px;
  }

  .share-icon-container {
    padding: 25px;
  }

  .share-icon {
    width: 50px;
    height: 50px;
  }

  .icon-label {
    font-size: 15px;
  }

  .step-box {
    padding: 20px 25px;
  }

  .step-text {
    font-size: 16px;
  }

  .step-text strong {
    font-size: 17px;
  }

  .install-button {
    font-size: 16px;
    padding: 14px 28px;
  }

  .logo-sub {
    width: 120px;
  }
}

/* 작은 화면 (iPhone SE 등) */
@media (max-width: 375px) {
  .install-pwa-screen {
    padding: 30px 0 20px 0;
  }

  .logo-container {
    margin-bottom: 25px;
  }

  .info-container {
    margin-bottom: 25px;
  }

  .guide-steps {
    gap: 12px;
  }

  .share-icon-container {
    padding: 18px;
  }

  .share-icon {
    width: 45px;
    height: 45px;
  }

  .step-arrow {
    font-size: 28px;
    margin: 3px 0;
  }

  .step-box {
    padding: 15px 18px;
  }

  .logo-sub-container {
    padding: 15px 0;
  }

  .logo-sub {
    width: 100px;
  }
}

/* 아주 작은 화면 (높이가 낮은 기기) */
@media (max-height: 600px) {
  .install-pwa-screen {
    padding: 20px 0 15px 0;
  }

  .logo-container {
    margin-bottom: 20px;
  }

  .logo {
    width: 140px;
  }

  .info-container {
    margin-bottom: 20px;
  }

  .title {
    font-size: 22px;
    margin-bottom: 12px;
  }

  .description {
    font-size: 15px;
  }

  .guide-steps {
    gap: 10px;
  }

  .share-icon-container {
    padding: 15px;
    gap: 10px;
  }

  .share-icon {
    width: 40px;
    height: 40px;
  }

  .icon-label {
    font-size: 14px;
  }

  .step-arrow {
    font-size: 24px;
    margin: 2px 0;
  }

  .step-box {
    padding: 12px 15px;
  }

  .step-text {
    font-size: 14px;
  }

  .step-text strong {
    font-size: 15px;
  }

  .logo-sub-container {
    padding: 10px 0;
  }

  .logo-sub {
    width: 90px;
  }
}
</style>


